import { useEffect, useRef, useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import DisconnectConfirmDialog from './DisconnectConfirmDialog.js'
import { api } from '../../../api/client.js'
import { useChannelsOnboarding } from '../../../hooks/useChannelsOnboarding.js'
import { useToast } from '../../system/ToastProvider.js'
import { PROVIDER_CARD_LABELS as PROVIDER_LABELS, PROVIDER_ICONS_LARGE } from '../../../pages/admin/channel-provider-meta.js'
import type { ChannelProviderName, ChannelStatusAsset } from '../../../pages/admin/channels-types.js'
import { timestampMs } from '../../../utils/timezone.js'

export interface ChannelProviderCardProps {
  provider: ChannelProviderName
  asset: ChannelStatusAsset | null
  onComplete: () => Promise<void>
  onPendingAssetSelection?: (assets: Array<{ id: string; name: string }>) => void
}

interface ApiErrorLike extends Error {
  status?: number
}

const DAY_MS = 24 * 60 * 60 * 1000
const WARN_WINDOW_MS = 7 * DAY_MS

function formatExpiry(iso: string | null): { label: string; severity: 'ok' | 'warn' | 'expired' | 'unknown' } {
  if (!iso) return { label: 'Token expiry unknown', severity: 'unknown' }
  const diffMs = timestampMs(iso) - Date.now()
  if (diffMs <= 0) return { label: 'Token expired — reconnect required', severity: 'expired' }
  const days = Math.floor(diffMs / DAY_MS)
  if (diffMs <= WARN_WINDOW_MS) {
    return { label: `Token refresh pending (expires in ${days} day${days === 1 ? '' : 's'})`, severity: 'warn' }
  }
  return { label: `Expires in ${days} day${days === 1 ? '' : 's'}`, severity: 'ok' }
}

export default function ChannelProviderCard(props: ChannelProviderCardProps): JSX.Element {
  const toast = useToast()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [securityModalOpen, setSecurityModalOpen] = useState(false)

  const label = PROVIDER_LABELS[props.provider]
  const isConnected = props.asset !== null && props.asset.connected

  const { state: onboardingState, error: onboardingError, launch: launchOnboarding, cancel: cancelOnboarding } = useChannelsOnboarding({
    provider: props.provider,
    onComplete: props.onComplete,
    onPendingAssetSelection: props.onPendingAssetSelection,
  })

  // Surface onboarding-state transitions as toasts instead of inline banners.
  const lastOnboardingStateRef = useRef(onboardingState)
  useEffect(() => {
    if (lastOnboardingStateRef.current === onboardingState) return
    lastOnboardingStateRef.current = onboardingState
    if (onboardingState === 'failed') {
      toast.error(`${label} connection failed`, onboardingError ?? 'Connection failed. Please try again.')
    } else if (onboardingState === 'timeout') {
      toast.warning(`${label} connection timed out`, 'Connection timed out. Please try again.')
    }
  }, [onboardingState, onboardingError, label, toast])

  async function handleConfirmDisconnect(): Promise<void> {
    if (!props.asset) return
    setDisconnecting(true)
    try {
      await api.delete(`/admin/channels/${props.provider}/${props.asset.id}`)
      setConfirmOpen(false)
      await props.onComplete()
      toast.success(`${label} disconnected`)
    } catch (e) {
      const err = e as ApiErrorLike
      if (err.status === 404) {
        toast.info(`${label} was already disconnected.`)
        setConfirmOpen(false)
        await props.onComplete()
      } else {
        toast.error(`Failed to disconnect ${label}`, err.message)
      }
    } finally {
      setDisconnecting(false)
    }
  }

  const isLoading = onboardingState === 'opening' || onboardingState === 'awaiting' || onboardingState === 'pending_asset_selection' || disconnecting

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
      <div className="flex items-center gap-4 pb-4 mb-6 border-b border-gray-100">
        {PROVIDER_ICONS_LARGE[props.provider]}
        <div>
          <h2 className="text-xl font-bold text-gray-900">{label}</h2>
          <p className="text-gray-500 text-[15px]">Connect your {label} Page to send messages and manage conversations.</p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-8 items-center md:items-start max-w-4xl">
        {/* Left Illustration Box */}
        <div className="w-[200px] h-[200px] rounded-full border-2 border-dashed border-blue-100 flex items-center justify-center shrink-0 relative bg-blue-50/20">
          <div className="absolute inset-0 m-auto w-24 h-16 bg-blue-100/40 rounded-lg border border-blue-200 flex flex-col justify-between p-2 shadow-sm">
             <div className="flex gap-1 mb-1">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-300"></div>
                <div className="w-1.5 h-1.5 rounded-full bg-blue-300"></div>
                <div className="w-1.5 h-1.5 rounded-full bg-blue-300"></div>
             </div>
             <div className="flex-1 bg-white rounded flex items-center justify-center text-blue-500">
                {props.provider === 'facebook' && <i className="pi pi-facebook" />}
                {props.provider === 'instagram' && <i className="pi pi-camera" />}
                {props.provider === 'whatsapp' && <i className="pi pi-whatsapp" />}
                {props.provider === 'youtube' && <i className="pi pi-youtube" />}
                {props.provider === 'x' && <span className="font-bold text-xs">X</span>}
             </div>
          </div>
          <div className="absolute bottom-6 right-6 w-8 h-8 rounded-full bg-blue-100 border-2 border-white flex items-center justify-center text-blue-600 shadow-sm">
             <i className="pi pi-link text-xs transform -rotate-45" />
          </div>
        </div>

        {/* Right Content */}
        <div className="flex-1">
          {isConnected && props.asset !== null ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium">
                <div className="w-2.5 h-2.5 bg-green-500 rounded-full"></div> 
                <span className="text-gray-900">Connected</span>
              </div>
              <p className="text-gray-600 text-[15px]">
                Successfully connected to account: <span className="font-semibold text-gray-900">{props.asset.accountName}</span>
              </p>
              
              <div className="text-sm">
                <span className="text-gray-500">Asset ID: </span>
                <code className="text-[13px] bg-gray-50 px-1 py-0.5 rounded">{props.asset.id}</code>
              </div>

              {(() => {
                const { label: text, severity } = formatExpiry(props.asset.tokenExpiry)
                if (severity === 'expired') {
                  return <div className="rounded bg-red-50 px-3 py-2 text-sm font-medium text-red-700">{text}</div>
                }
                if (severity === 'warn') {
                  return <div className="rounded bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800">{text}</div>
                }
                if (severity === 'unknown') {
                  return <div className="text-sm text-gray-500">{text}</div>
                }
                return <div className="text-sm text-gray-500">{text}</div>
              })()}

              <div className="pt-2">
                <Button
                  label="Disconnect"
                  className="bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 px-4 py-2 text-sm font-medium rounded-lg"
                  onClick={() => setConfirmOpen(true)}
                  disabled={isLoading}
                  icon="pi pi-power-off"
                />
              </div>

              <DisconnectConfirmDialog
                visible={confirmOpen}
                displayName={`${label}: ${props.asset.accountName}`}
                pending={disconnecting}
                onCancel={() => setConfirmOpen(false)}
                onConfirm={() => void handleConfirmDisconnect()}
              />
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2 text-sm font-medium mb-3">
                <div className="w-2.5 h-2.5 bg-gray-300 rounded-full"></div> 
                <span className="text-gray-900">Not connected</span>
              </div>
              <p className="text-gray-600 text-[15px] mb-5">
                You haven't connected any {label} accounts yet.
              </p>
              
              <ul className="space-y-3 mb-6">
                <li className="flex gap-2.5 items-center text-gray-700 text-sm">
                  <i className="pi pi-check text-green-500 font-bold"></i> Manage inbox and messages
                </li>
                <li className="flex gap-2.5 items-center text-gray-700 text-sm">
                  <i className="pi pi-check text-green-500 font-bold"></i> Respond to comments and DMs
                </li>
              </ul>
              
              <div className="flex items-center gap-3">
                <Button
                  label={isLoading ? 'Connecting...' : `Connect ${label}`}
                  className="bg-[#0b1f3c] text-white border-none hover:bg-gray-800 px-5 py-2.5 text-sm font-medium rounded-lg disabled:opacity-75"
                  icon={isLoading ? "pi pi-spin pi-spinner" : `pi pi-${props.provider === 'x' ? 'twitter' : props.provider === 'youtube' ? 'youtube' : props.provider === 'instagram' ? 'camera' : props.provider}`}
                  onClick={() => void launchOnboarding()}
                  disabled={isLoading}
                />
                {onboardingState === 'awaiting' && (
                  <Button
                    label="Cancel"
                    className="bg-gray-100 text-gray-700 border-none px-4 py-2.5 text-sm font-medium rounded-lg hover:bg-gray-200"
                    onClick={cancelOnboarding}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Security Banner */}
      <div className="mt-8 bg-[#f8faff] rounded-xl p-4 flex justify-between items-center border border-[#e5eafd]">
        <div className="flex gap-4 items-center">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 text-xl">
            <i className="pi pi-shield"></i>
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 text-[15px]">We keep your data safe</h3>
            <p className="text-gray-500 text-sm mt-0.5">Your data is encrypted and your tokens are stored securely.</p>
          </div>
        </div>
        <button
          onClick={() => setSecurityModalOpen(true)}
          className="hidden sm:flex text-blue-600 text-sm font-medium hover:underline items-center gap-1.5 group cursor-pointer bg-transparent border-none"
        >
          Learn more <i className="pi pi-external-link text-[11px] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform delay-75"></i>
        </button>
      </div>

      {/* Security Info Modal */}
      <Dialog
        visible={securityModalOpen}
        onHide={() => setSecurityModalOpen(false)}
        header="Security & Privacy"
        className="w-full max-w-lg"
        modal
        dismissableMask
      >
        <div className="space-y-4 text-sm">
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center text-green-600 shrink-0">
              <i className="pi pi-lock text-xs"></i>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">End-to-End Encryption</h4>
              <p className="text-gray-600 mt-1">All OAuth tokens and credentials are encrypted using AES-256 encryption before storage.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 shrink-0">
              <i className="pi pi-key text-xs"></i>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Key Management</h4>
              <p className="text-gray-600 mt-1">Encryption keys are managed using a Key Encryption Key (KEK) system with versioning for secure key rotation.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 shrink-0">
              <i className="pi pi-shield text-xs"></i>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">OAuth 2.0 Security</h4>
              <p className="text-gray-600 mt-1">We use industry-standard OAuth 2.0 with PKCE for X/Twitter to prevent authorization code interception.</p>
            </div>
          </div>
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-600 shrink-0">
              <i className="pi pi-database text-xs"></i>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900">Tenant Isolation</h4>
              <p className="text-gray-600 mt-1">Each tenant's data is stored in isolated schemas with strict access controls and tenant-scoped routing.</p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-200">
            <p className="text-gray-500 text-xs">
              For detailed security documentation, please contact your system administrator.
            </p>
          </div>
        </div>
      </Dialog>
    </div>
  )
}
